import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import CustomMultipleSelect from 'Components/CustomMultipleSelect/CustomMultipleSelect';
import CustomSelect from 'Components/CustomSelect/CustomSelect';
import { Controller, useForm } from 'react-hook-form';
import CoursesTable, { type CoursesTableHandle } from './CoursesTable';
import useTerminalsApi from 'Hooks/useTerminalsApi';
import useDepartmentsApi from 'Hooks/useDepartmentsApi';
import useJobTitlesApi from 'Hooks/useJobTitlesApi';
import CustomDatePicker from 'Components/CustomDatePicker/CustomDatePicker';
import userCourseHistoryApi from 'Api/axios/user-course-histories-api';
import { downloadFile } from 'Utils/file-utils';
import { useRef, useState } from 'react';

interface MassiveExportDialogProps {
  open: boolean;
  onClose(): void;
}

const MassiveExportDialog = ({ open, onClose }: MassiveExportDialogProps) => {
  const coursesTableRef = useRef<CoursesTableHandle>(null);
  const [loading, setLoading] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);

  const {
    control,
    reset,
    getValues,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      terminalId: [],
      departmentId: null,
      jobTitleId: null,
      finishDate: null,
    },
  });

  const { data: terminals } = useTerminalsApi({ init: true });

  const { data: departments } = useDepartmentsApi({ init: true });

  const { data: jobTitles } = useJobTitlesApi({ init: true });

  const onCloseDialog = () => {
    reset();
    onClose();
  };

  const handleGenerateReport = async () => {
    const courseIds = coursesTableRef.current?.getSelectedIds() ?? [];
    if (courseIds.length === 0) return;

    const { terminalId, departmentId, jobTitleId, finishDate } = getValues();

    setLoading(true);
    try {
      const response = await userCourseHistoryApi.exportCoursesReport(courseIds, {
        terminalId,
        departmentId,
        jobTitleId,
        finishedDate: finishDate?.toISODate() ?? undefined,
      });

      if (response.data) {
        downloadFile(response.data, 'reporte_masivo_cursos.xlsx');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCloseDialog}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { maxHeight: '95vh', overflow: 'auto' } }}
    >
      <DialogTitle>Generación de Reporte</DialogTitle>
      <Divider />
      <DialogContent>
        <DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body1" gutterBottom>
              Selecciona los cursos que deseas incluir en el reporte. Puedes aplicar filtros para refinar tu selección y
              asegurarte de que el reporte generado contenga solo la información relevante para ti.
            </Typography>
            <CoursesTable ref={coursesTableRef} onSelectionChange={setSelectedCount} />
            <Box display="flex" flexDirection="row" gap="20px" alignItems="end" marginTop="20px">
              <Controller
                name="finishDate"
                control={control}
                render={({ field }) => (
                  <CustomDatePicker label="Fecha de finalización" placeholder="dd/mm/yyyy" {...field} />
                )}
              />

              <Controller
                name="terminalId"
                control={control}
                render={({ field }) => (
                  <CustomMultipleSelect
                    placeholder={'Terminal'}
                    options={terminals.map(terminal => ({ label: terminal.name, value: terminal.id }))}
                    {...field}
                  />
                )}
              />
              <Controller
                name="departmentId"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    placeholder={'Departamento'}
                    options={departments.map(department => ({ label: department.name, value: department.id }))}
                    {...field}
                  />
                )}
              />
              <Controller
                name="jobTitleId"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    placeholder={'Puesto'}
                    options={jobTitles.map(jobTitle => ({ label: jobTitle.name, value: jobTitle.id }))}
                    {...field}
                  />
                )}
              />
              {isDirty && (
                <Button variant="text" color="error" size="small" onClick={() => reset()}>
                  Limpiar filtros
                </Button>
              )}
            </Box>
            <Divider sx={{ marginY: '20px' }} />
            <Box display="flex" flexDirection="row" gap="10px" justifyContent="end">
              <Button variant="outlined" color="error" onClick={onCloseDialog}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleGenerateReport}
                disabled={loading || selectedCount === 0}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Generar Reporte'}
              </Button>
            </Box>
          </Box>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default MassiveExportDialog;
